<template>
    <div class="timezone-section">
        <div class="column">
            <div v-tooltip="props.timezoneSelectionLocked ? 'Timezone selection unavailable while set by dashboard' : ''" class="timezone-group">
                <div class="checkbox-label-wrapper">
                    <checkbox
                        v-model="inheritTimezoneFromSitewide"
                        binary
                        input-id="inheritTimezoneCheckbox"
                        :disabled="props.timezoneSelectionLocked"
                        @update:model-value="updateInheritTimezoneFromSitewide"
                    />
                    <label for="inheritTimezoneCheckbox">Inherit timezone from sitewide settings</label>
                </div>
                <time-zone-selector
                    v-model="selectedTimezoneOption"
                    :disabled="inheritTimezoneFromSitewide || props.timezoneSelectionLocked"
                    iana
                />
                <em class="inherited-value">
                    Current sitewide: {{ props.sitewideTimezone }}
                </em>
            </div>
            <div
                v-if="!props.hidePeriodFrequency"
                v-tooltip="props.periodFrequencySelectionLocked ? 'Period frequency selection unavailable while set by dashboard' : ''"
                class="timezone-group"
            >
                <div class="checkbox-label-wrapper">
                    <checkbox
                        v-model="inheritPeriodFrequencyFromSitewide"
                        binary
                        input-id="inheritPeriodsCheckbox"
                        :disabled="props.periodFrequencySelectionLocked"
                        @update:model-value="updateInheritPeriodFrequencyFromSitewide"
                    />
                    <label for="inheritPeriodsCheckbox">Inherit period frequency from sitewide setting</label>
                </div>
                <select-button
                    v-model="selectedPeriodsPerDayOption"
                    :options="PERIOD_FREQUENCY_OPTIONS"
                    option-label="name"
                    option-value="id"
                    :disabled="inheritPeriodFrequencyFromSitewide || props.periodFrequencySelectionLocked"
                >
                    <template #option="{ option }">
                        <span>{{ option.name }}</span>
                        <info-icon-with-tooltip v-if="option.info" :tooltip-text="option.info" />
                    </template>
                </select-button>
                <em class="inherited-value">
                    Current sitewide: {{ props.sitewidePeriodsPerDayLabel }}
                </em>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Checkbox from 'primevue/checkbox';
import SelectButton from 'primevue/selectbutton';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { PERIOD_FREQUENCY_OPTIONS } from '@/constants/PeriodFrequencyOptions.ts';
import TimeZoneSelector from '@/components/powerMarkets/selectors/TimeZoneSelector.vue';
import { DEFAULT_TIMEZONE } from '@/constants/Time.ts';

const props = defineProps<{
    timezoneId?: string;
    periodFrequency?: number;
    timezoneSelectionLocked?: boolean;
    periodFrequencySelectionLocked?: boolean;
    sitewideTimezone: string;
    sitewidePeriodsPerDay: number;
    sitewidePeriodsPerDayLabel: string;
    hidePeriodFrequency?: boolean;
}>();

const DEFAULT_PERIODS_PER_DAY = '48';

const selectedTimezoneOption = ref<IIdName>({ id: DEFAULT_TIMEZONE, name: DEFAULT_TIMEZONE });
const inheritTimezoneFromSitewide = ref<boolean>(true);
const selectedPeriodsPerDayOption = ref<string>(DEFAULT_PERIODS_PER_DAY);
const inheritPeriodFrequencyFromSitewide = ref<boolean>(true);

function setTimezoneFromProps (): void {
    if (props.timezoneId) {
        selectedTimezoneOption.value = { id: props.timezoneId, name: props.timezoneId };
        inheritTimezoneFromSitewide.value = false;
    } else {
        selectedTimezoneOption.value = { id: props.sitewideTimezone, name: props.sitewideTimezone };
        inheritTimezoneFromSitewide.value = true;
    }
}

function setPeriodsPerDayFromProps (): void {
    if (props.periodFrequency !== undefined && props.periodFrequency !== null) {
        selectedPeriodsPerDayOption.value = props.periodFrequency.toString();
        inheritPeriodFrequencyFromSitewide.value = false;
        return;
    }

    selectedPeriodsPerDayOption.value = props.sitewidePeriodsPerDay.toString();
    inheritPeriodFrequencyFromSitewide.value = true;
}

function updateInheritTimezoneFromSitewide (value: boolean): void {
    inheritTimezoneFromSitewide.value = value;
    if (value) {
        selectedTimezoneOption.value = { id: props.sitewideTimezone, name: props.sitewideTimezone };
    }
}

function updateInheritPeriodFrequencyFromSitewide (value: boolean): void {
    inheritPeriodFrequencyFromSitewide.value = value;
    if (value) {
        selectedPeriodsPerDayOption.value = props.sitewidePeriodsPerDay.toString();
    }
}

function getPayload (): { timezone: string | null; periodsPerDay: number | null } {
    return {
        timezone: inheritTimezoneFromSitewide.value ? null : selectedTimezoneOption.value.id,
        periodsPerDay: props.hidePeriodFrequency || inheritPeriodFrequencyFromSitewide.value ? null : Number(selectedPeriodsPerDayOption.value),
    };
}

setTimezoneFromProps();
setPeriodsPerDayFromProps();

defineExpose({
    getPayload,
});

watch(() => props.timezoneId, () => {
    setTimezoneFromProps();
});

watch(() => props.periodFrequency, () => {
    setPeriodsPerDayFromProps();
});

watch(() => props.sitewideTimezone, (newTimezone) => {
    if (inheritTimezoneFromSitewide.value) {
        selectedTimezoneOption.value = { id: newTimezone, name: newTimezone };
    }
});

watch(() => props.sitewidePeriodsPerDay, (newPeriodFrequency) => {
    if (inheritPeriodFrequencyFromSitewide.value) {
        selectedPeriodsPerDayOption.value = newPeriodFrequency.toString();
    }
});

</script>

<style lang="scss" scoped>
.timezone-section {
    padding: var(--defaultSpacing);

    .column {
        display: flex;
        flex-direction: column;
        gap: var(--largeSpacing);
    }

    .timezone-group {
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);
        margin-bottom: var(--largeSpacing);

        .checkbox-label-wrapper {
            display: flex;
            gap: var(--defaultSpacing);
            align-items: center;

            label {
                margin: 0;
                white-space: nowrap;
            }
        }
    }

    .inherited-value {
        font-size: var(--smallFontSize);
        color: var(--neutral600);
        font-style: italic;
    }

    :deep(.p-select.p-disabled) {
        background: var(--p-inputtext-disabled-background);
        color: var(--p-inputtext-disabled-color);
    }
}
</style>
